// src/App.js
import { useEffect, useState, useCallback } from "react";
import { BrowserRouter, Routes, Route, useNavigate } from "react-router-dom";
import { AuthProvider } from "./context/AuthContext";
import { SessionFeatureFlags } from "./context/SessionFeatureFlags";
import { DataProvider } from "./context/DataContext";
import { NotificationsProvider } from "./context/NotificationsContext";
import { FeatureFlagsProvider } from "./context/FeatureFlagsContext";
import { BrandProvider, useBrand, useBrandReady } from "./context/BrandContext";
import { ToastProvider } from "./context/ToastContext";
import { ThemeProvider } from "./context/ThemeContext";
import AppLoadingScreen from "./componants/common/AppLoadingScreen";
import DemoBanner from "./componants/common/DemoBanner";
import DemoEntry from "./componants/common/DemoEntry";
import LoginPage from "./componants/LoginPage";
import PublicCareers from "./componants/careers/PublicCareers";
import PublicJobApply from "./componants/careers/PublicJobApply";
import ProductLanding from "./componants/landing/ProductLanding";
import RootEntry from "./componants/landing/RootEntry";
import { SALES_LANDING_PATH } from "./config/marketing";
import EssHome from "./componants/common/EssHome";
import ProtectedRoute from "./componants/common/ProtectedRoute";
import ChangePasswordRoute from "./componants/common/ChangePasswordRoute";
import NoInternetScreen from "./componants/common/NoInternetScreen";
import ForceUpdateScreen from "./componants/common/ForceUpdateScreen";
import {
  initNetworkMonitor,
  addNetworkListener,
  getNetworkStatus,
  refreshNetworkStatus,
  cleanupNetworkMonitor,
} from "./utils/networkMonitor";
import { registerAppListeners } from "./utils/mobile";
import { checkAppUpdate } from "./utils/appUpdate";

// ─── Deep Link Handler (must be inside BrowserRouter) ────────────────────────
function DeepLinkHandler() {
  const navigate = useNavigate();

  useEffect(() => {
    const handleDeepLink = (url) => {
      try {
        const parsed = new URL(url);
        // Map deep link paths → app routes
        const path = parsed.pathname || "/";
        navigate(path, { replace: true });
      } catch {
        // Ignore invalid URLs
      }
    };

    let cleanup = () => {};
    registerAppListeners({ onDeepLink: handleDeepLink }).then((fn) => {
      cleanup = fn;
    });

    return () => cleanup();
  }, [navigate]);

  return null;
}

// Let a slow version check finish in the background instead of freezing
// the shell on a weak network. Force-update still takes over if it arrives.
const UPDATE_CHECK_GRACE_MS = 4000;

// ─── Network-Aware App Shell ──────────────────────────────────────────────────
function AppShell() {
  const [isOnline, setIsOnline] = useState(() => getNetworkStatus().connected);
  const [updateInfo, setUpdateInfo] = useState(null);
  // Web never force-updates — avoid a blank first frame waiting on the API.
  const [updateChecked, setUpdateChecked] = useState(() => {
    try {
      return !window.Capacitor?.isNativePlatform?.();
    } catch {
      return true;
    }
  });

  const runUpdateCheck = useCallback(async () => {
    const result = await checkAppUpdate();
    setUpdateInfo(result);
    setUpdateChecked(true);
    return result;
  }, []);

  useEffect(() => {
    initNetworkMonitor();

    const removeListener = addNetworkListener(({ connected }) => {
      setIsOnline(connected);
    });

    return () => {
      removeListener();
      cleanupNetworkMonitor();
    };
  }, []);

  useEffect(() => {
    if (!isOnline) return undefined;

    let cancelled = false;
    const graceId = window.setTimeout(() => {
      if (!cancelled) setUpdateChecked(true);
    }, UPDATE_CHECK_GRACE_MS);

    runUpdateCheck().finally(() => {
      if (!cancelled) window.clearTimeout(graceId);
    });

    const onResume = () => {
      runUpdateCheck();
    };
    window.addEventListener("app:resume", onResume);

    const onForceUpdate = (event) => {
      const detail = event?.detail;
      if (!detail?.updateRequired) return;
      setUpdateInfo((prev) => ({
        updateRequired: true,
        localVersion: prev?.localVersion || "",
        localVersionCode: prev?.localVersionCode || 0,
        latestVersion: detail.latestVersion || prev?.latestVersion || "",
        latestVersionCode:
          detail.latestVersionCode || prev?.latestVersionCode || 0,
        apkUrl: detail.apkUrl || prev?.apkUrl || "",
        message: detail.message || prev?.message || "",
      }));
      setUpdateChecked(true);
    };
    window.addEventListener("app:force-update", onForceUpdate);

    // Register native resume even when ForceUpdateScreen blocks BrowserRouter
    // (DeepLinkHandler is not mounted in that state).
    let nativeResumeHandle = null;
    (async () => {
      try {
        const { isNative } = await import("./utils/mobile");
        if (!isNative() || cancelled) return;
        const { App } = await import("@capacitor/app");
        if (cancelled) return;
        nativeResumeHandle = await App.addListener("resume", () => {
          window.dispatchEvent(new CustomEvent("app:resume"));
        });
      } catch {
        // Capacitor App plugin unavailable
      }
    })();

    return () => {
      cancelled = true;
      window.clearTimeout(graceId);
      window.removeEventListener("app:resume", onResume);
      window.removeEventListener("app:force-update", onForceUpdate);
      nativeResumeHandle?.remove?.();
    };
  }, [isOnline, runUpdateCheck]);

  const handleRetry = useCallback(() => {
    refreshNetworkStatus().then((status) => {
      setIsOnline(status.connected);
    });
  }, []);

  if (!isOnline) {
    return <NoInternetScreen onRetry={handleRetry} />;
  }

  if (!updateChecked) {
    return <AppLoadingScreen message="جارٍ التحقق من التحديثات..." />;
  }

  if (updateInfo?.updateRequired) {
    return (
      <ForceUpdateScreen
        message={updateInfo.message}
        apkUrl={updateInfo.apkUrl}
        latestVersion={updateInfo.latestVersion}
        latestVersionCode={updateInfo.latestVersionCode}
        localVersion={updateInfo.localVersion}
        localVersionCode={updateInfo.localVersionCode}
        onRecheck={runUpdateCheck}
      />
    );
  }

  return (
    <BrowserRouter>
      <DemoBanner />
      <DeepLinkHandler />
      <Routes>
        <Route path="/" element={<RootEntry />} />
        <Route path={SALES_LANDING_PATH} element={<ProductLanding />} />
        <Route path="/demo" element={<DemoEntry />} />
        <Route path="/login" element={<LoginPage />} />
        <Route path="/platform" element={<LoginPage platformMode />} />
        <Route path="/careers" element={<PublicCareers />} />
        <Route path="/careers/:id" element={<PublicJobApply />} />
        <Route path="/change-password" element={<ChangePasswordRoute />} />
        <Route
          path="/*"
          element={
            <ProtectedRoute>
              <EssHome />
            </ProtectedRoute>
          }
        />
      </Routes>
    </BrowserRouter>
  );
}

// ─── Brand-driven Feature Flags bridge ────────────────────────────────────────
// useBrand().featureFlags already carries the full priority chain — runtime
// manifest (window.__BRAND_MANIFEST__ or the fetched
// brand-manifest.generated.json) overriding build-time REACT_APP_FEATURE_*
// overriding the hardcoded ESS defaults (see runtimeBrandConfig.js). Feeding
// that into the existing FeatureFlagsProvider means useFeatureFlags() picks
// up runtime-manifest overrides automatically, with zero changes to
// FeatureFlagsContext.js itself or to any screen that already calls
// useFeatureFlags()/FeatureGate. Must render inside <BrandProvider> to call
// useBrand().
function BrandedFeatureFlags({ children }) {
  const brand = useBrand();
  return (
    <FeatureFlagsProvider flags={brand.featureFlags}>
      {children}
    </FeatureFlagsProvider>
  );
}

// Holds the rest of the tree until BrandProvider has finished the runtime
// manifest fetch, so Login / sidebar / feature flags never paint ESS (or
// any other stale snapshot) and then swap to the real brand a moment later.
function BrandBootGate({ children }) {
  const isReady = useBrandReady();
  if (!isReady) {
    return (
      <AppLoadingScreen
        message="جارٍ تحميل بيانات الشركة..."
      />
    );
  }
  return children;
}

// ─── Root Component ───────────────────────────────────────────────────────────
function App() {
  return (
    <ThemeProvider>
      <BrandProvider>
        <BrandBootGate>
          <BrandedFeatureFlags>
            <ToastProvider>
            <AuthProvider>
              <SessionFeatureFlags>
              <DataProvider>
                <NotificationsProvider>
                  <AppShell />
                </NotificationsProvider>
              </DataProvider>
              </SessionFeatureFlags>
            </AuthProvider>
            </ToastProvider>
          </BrandedFeatureFlags>
        </BrandBootGate>
      </BrandProvider>
    </ThemeProvider>
  );
}

export default App;
